import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { listActiveRoles } from '../../services/roleAdministration';
import {
  listUserPerformanceReportDivisions,
  listUserPerformanceReportUsers,
  isUserPerformanceReportRole,
  type UserPerformanceReportDivision,
  type UserPerformanceReportUser,
} from '../../services/userPerformanceReports';

function mergeUsers(users: Iterable<UserPerformanceReportUser>): UserPerformanceReportUser[] {
  const seen = new Map<string, UserPerformanceReportUser>();
  for (const user of users) seen.set(user.id, user);
  return [...seen.values()].sort((a, b) => a.displayName.localeCompare(b.displayName));
}

export function useUserPerformanceLookups(selectedRoleNames: string[]) {
  const { user } = useAuth();
  const [allUsers, setAllUsers] = useState<UserPerformanceReportUser[]>([]);
  const [usersByRoleId, setUsersByRoleId] = useState<Partial<Record<number, UserPerformanceReportUser[]>>>({});
  const [divisionOptions, setDivisionOptions] = useState<UserPerformanceReportDivision[]>([]);
  const [roleOptions, setRoleOptions] = useState<{ id: number; name: string }[]>([]);
  const [lookupsLoaded, setLookupsLoaded] = useState(false);
  const [allUsersResolved, setAllUsersResolved] = useState(false);
  const [loadingRoleIds, setLoadingRoleIds] = useState<number[]>([]);
  const usersByRoleIdRef = useRef(usersByRoleId);

  useEffect(() => {
    usersByRoleIdRef.current = usersByRoleId;
  }, [usersByRoleId]);

  const selectedRoleIds = useMemo(
    () =>
      selectedRoleNames
        .map((name) => roleOptions.find((r) => r.name === name)?.id)
        .filter((id): id is number => typeof id === 'number'),
    [roleOptions, selectedRoleNames],
  );

  const userRequestKey = useMemo(
    () => JSON.stringify([...selectedRoleIds].sort((a, b) => a - b)),
    [selectedRoleIds],
  );

  const usersLoading =
    selectedRoleIds.length === 0
      ? lookupsLoaded && !allUsersResolved
      : lookupsLoaded && selectedRoleIds.some((id) => usersByRoleId[id] === undefined || loadingRoleIds.includes(id));

  useEffect(() => {
    if (!user?.id) return;
    let alive = true;

    void Promise.all([listUserPerformanceReportDivisions(), listActiveRoles()]).then(([divisions, roles]) => {
      if (!alive) return;
      if (divisions.success) setDivisionOptions(divisions.data);
      if (roles.success) {
        setRoleOptions(
          roles.data
            .filter(isUserPerformanceReportRole)
            .map((r) => ({ id: r.roleId, name: r.roleName }))
            .filter((r) => r.name.trim())
            .sort((a, b) => a.name.localeCompare(b.name)),
        );
      }
      setLookupsLoaded(true);
    });

    return () => {
      alive = false;
    };
  }, [user?.id]);

  useEffect(() => {
    if (!lookupsLoaded) return;

    if (selectedRoleIds.length > 0) {
      setAllUsersResolved(false);
      return;
    }

    let alive = true;
    const controller = new AbortController();
    setAllUsersResolved(false);

    void listUserPerformanceReportUsers([], controller.signal).then((users) => {
      if (!alive || controller.signal.aborted) return;
      if (users.success) setAllUsers(users.data);
      else setAllUsers([]);
      setAllUsersResolved(true);
    });

    return () => {
      alive = false;
      controller.abort();
    };
  }, [lookupsLoaded, userRequestKey, selectedRoleIds.length]);

  useEffect(() => {
    if (!lookupsLoaded || selectedRoleIds.length === 0) return;

    const controller = new AbortController();
    const missingRoleIds = selectedRoleIds.filter((id) => usersByRoleIdRef.current[id] === undefined);
    if (!missingRoleIds.length) return;

    setLoadingRoleIds((prev) => [...new Set([...prev, ...missingRoleIds])]);

    void Promise.all(
      missingRoleIds.map(async (roleId) => {
        const users = await listUserPerformanceReportUsers([roleId], controller.signal);
        return { roleId, users };
      }),
    ).then((results) => {
      if (controller.signal.aborted) return;
      setUsersByRoleId((prev) => {
        const next = { ...prev };
        for (const { roleId, users } of results) {
          next[roleId] = users.success ? users.data : [];
        }
        return next;
      });
      setLoadingRoleIds((prev) => prev.filter((id) => !missingRoleIds.includes(id)));
    });

    return () => controller.abort();
  }, [lookupsLoaded, userRequestKey, selectedRoleIds]);

  const userOptions = useMemo(() => {
    if (selectedRoleIds.length === 0) return allUsers;
    const merged: UserPerformanceReportUser[] = [];
    for (const roleId of selectedRoleIds) {
      const users = usersByRoleId[roleId];
      if (users) merged.push(...users);
    }
    return mergeUsers(merged);
  }, [allUsers, selectedRoleIds, usersByRoleId]);

  const filterSelectedUserIds = useCallback(
    (selectedIds: string[], roleNames: string[]) => {
      const roleIds = roleNames
        .map((name) => roleOptions.find((r) => r.name === name)?.id)
        .filter((id): id is number => typeof id === 'number');

      const validIds = new Set<string>();
      if (roleIds.length === 0) {
        for (const u of allUsers) validIds.add(u.id);
      } else {
        for (const roleId of roleIds) {
          for (const u of usersByRoleId[roleId] ?? []) validIds.add(u.id);
        }
      }

      return selectedIds.filter((id) => validIds.has(id));
    },
    [allUsers, roleOptions, usersByRoleId],
  );

  const userOptionItems = useMemo(
    () => userOptions.map((u) => ({ value: u.id, label: u.displayName })),
    [userOptions],
  );
  const divisionNames = useMemo(() => divisionOptions.map((d) => d.name), [divisionOptions]);
  const roleNames = useMemo(() => roleOptions.map((r) => r.name), [roleOptions]);

  const mapRoleNamesToIds = (names: string[]) =>
    names
      .map((name) => roleOptions.find((r) => r.name === name)?.id)
      .filter((id): id is number => typeof id === 'number');

  const mapDivisionNamesToIds = (names: string[]) =>
    names
      .map((name) => divisionOptions.find((d) => d.name === name)?.id)
      .filter((id): id is number => typeof id === 'number');

  return {
    userOptionItems,
    divisionNames,
    roleNames,
    mapRoleNamesToIds,
    mapDivisionNamesToIds,
    filterSelectedUserIds,
    lookupsLoading: !lookupsLoaded,
    usersLoading,
  };
}
